import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ShieldCheck, Loader2, X, CreditCard, Link2, Copy, MessageSquareText } from "lucide-react";
import { smsHref, firstName } from "@/lib/sms";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SQUARE_JS = {
  production: "https://web.squarecdn.com/v1/square.js",
  sandbox: "https://sandbox.web.squarecdn.com/v1/square.js",
};

const loadSquareJs = (env) =>
  new Promise((resolve, reject) => {
    if (window.Square) return resolve();
    const existing = document.querySelector('script[data-square-sdk]');
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject(new Error("Square SDK failed to load")));
      return;
    }
    const s = document.createElement("script");
    s.src = SQUARE_JS[env] || SQUARE_JS.production;
    s.async = true;
    s.dataset.squareSdk = "1";
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("Square SDK failed to load"));
    document.head.appendChild(s);
  });

/**
 * £100 refundable lodge security deposit controls.
 * mode "arrival"   → offers "Hold £100 deposit" (card form here) or "Send link"
 *                    (guest enters their own card on a branded page)
 * mode "departure" → offers Release / Charge for an active hold
 * All states render a status badge so the history is visible on both lists.
 */
const DepositControls = ({ booking, mode, onChanged }) => {
  const [showCard, setShowCard] = useState(false);
  const [cardReady, setCardReady] = useState(false);
  const [busy, setBusy] = useState(null); // "hold" | "release" | "charge" | "link"
  const [linkUrl, setLinkUrl] = useState(null);
  const [linkEmailed, setLinkEmailed] = useState(false);
  const cardRef = useRef(null);
  const cardContainerId = `sq-card-${booking.id}`;

  const status = booking.deposit_status || null;
  const expired =
    status === "held" &&
    booking.deposit_delayed_until &&
    new Date(booking.deposit_delayed_until) < new Date();

  useEffect(() => {
    if (!showCard) return undefined;
    let disposed = false;
    (async () => {
      try {
        const { data: cfg } = await axios.get(`${API}/admin/square-config`);
        await loadSquareJs(cfg.env);
        if (disposed) return;
        const payments = window.Square.payments(cfg.application_id, cfg.location_id);
        const card = await payments.card();
        await card.attach(`#${cardContainerId}`);
        if (disposed) {
          card.destroy?.();
          return;
        }
        cardRef.current = card;
        setCardReady(true);
      } catch (e) {
        toast.error(e?.response?.data?.detail || e.message || "Couldn't load the card form.");
        setShowCard(false);
      }
    })();
    return () => {
      disposed = true;
      cardRef.current?.destroy?.();
      cardRef.current = null;
      setCardReady(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCard]);

  const hold = async () => {
    if (!cardRef.current || busy) return;
    setBusy("hold");
    try {
      const result = await cardRef.current.tokenize();
      if (result.status !== "OK") {
        throw new Error(result.errors?.[0]?.message || "Card could not be verified — check the details.");
      }
      const { data } = await axios.post(`${API}/admin/bookings/${booking.id}/deposit/hold`, {
        source_id: result.token,
      });
      toast.success(`£100 deposit held${data.delayed_until ? ` · auto-releases ${new Date(data.delayed_until).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}` : ""}`);
      setShowCard(false);
      onChanged?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : e.message || "Couldn't place the hold.");
    } finally {
      setBusy(null);
    }
  };

  const sendLink = async () => {
    if (busy) return;
    setBusy("link");
    try {
      const { data } = await axios.post(`${API}/admin/bookings/${booking.id}/deposit/link`);
      setLinkUrl(data.url);
      setLinkEmailed(data.emailed);
      toast.success(
        data.emailed
          ? `Deposit link emailed to ${booking.guest_email}.`
          : "Deposit link ready — the email didn't go, so pass it on yourself."
      );
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't create the deposit link.");
    } finally {
      setBusy(null);
    }
  };

  const copyLink = async () => {
    if (!linkUrl) return;
    try {
      await navigator.clipboard.writeText(linkUrl);
      toast.success("Link copied — paste it into a text or WhatsApp.");
    } catch {
      toast.error("Couldn't copy — select the link and copy it manually.");
    }
  };

  const act = async (action) => {
    if (busy) return;
    const label = action === "release" ? "release the £100 hold (guest gets nothing charged)" : "CHARGE the £100 deposit — money will be taken from the guest's card";
    if (!window.confirm(`Are you sure you want to ${label}?`)) return;
    setBusy(action);
    try {
      await axios.post(`${API}/admin/bookings/${booking.id}/deposit/${action}`);
      toast.success(action === "release" ? "£100 hold released." : "£100 deposit charged.");
      onChanged?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : `Couldn't ${action} the deposit.`);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="mt-3 pt-3 border-t border-[#EDE3D2]" data-testid={`deposit-controls-${booking.id}`}>
      <p className="text-[10px] uppercase tracking-[0.18em] text-[#B76E79] font-semibold flex items-center gap-1">
        <ShieldCheck size={11} /> £100 refundable deposit
      </p>

      {status === "held" && !expired && (
        <div className="mt-1.5">
          <span className="inline-block px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.12em] font-semibold bg-[#C89B5F] text-white" data-testid={`deposit-status-${booking.id}`}>
            Held{booking.deposit_card ? ` · card ••${booking.deposit_card}` : ""}{booking.deposit_via === "guest-link" ? " · via link" : ""}
          </span>
          {booking.deposit_delayed_until && (
            <p className="mt-1 text-[10px] text-[#6A5F58]">
              Auto-releases {new Date(booking.deposit_delayed_until).toLocaleDateString("en-GB", { day: "numeric", month: "short" })} if untouched
            </p>
          )}
        </div>
      )}
      {status === "held" && expired && (
        <div className="mt-1.5">
          <span className="inline-block px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.12em] font-semibold bg-[#8C4E58] text-white" data-testid={`deposit-status-${booking.id}`}>
            Hold expired
          </span>
          <p className="mt-1 text-[10px] text-[#6A5F58]">Card holds last ~7 days — this one has lapsed</p>
        </div>
      )}
      {status === "released" && (
        <span className="mt-1.5 inline-block px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.12em] font-semibold bg-[#3B8C4E] text-white" data-testid={`deposit-status-${booking.id}`}>
          Released
        </span>
      )}
      {status === "charged" && (
        <span className="mt-1.5 inline-block px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.12em] font-semibold bg-[#8C4E58] text-white" data-testid={`deposit-status-${booking.id}`}>
          Charged
        </span>
      )}

      {mode === "departure" && !status && (
        <p className="mt-1.5 text-[10px] text-[#6A5F58] italic" data-testid={`deposit-status-${booking.id}`}>No hold was placed on arrival</p>
      )}

      {/* Link sent but guest hasn't completed it yet — so the office can see it's in flight */}
      {!status && booking.deposit_link_sent_at && (
        <p className="mt-1.5 text-[10px] text-[#6A5F58]" data-testid={`deposit-link-waiting-${booking.id}`}>
          Link sent {new Date(booking.deposit_link_sent_at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" })} — waiting for the guest · you're emailed the moment it's placed
        </p>
      )}

      {/* Arrival: place the hold here — or send the guest a link so they enter their own card */}
      {mode === "arrival" && (!status || expired) && !showCard && (
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          <button
            type="button"
            onClick={() => setShowCard(true)}
            data-testid={`deposit-hold-open-${booking.id}`}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-[#B76E79] text-[#B76E79] hover:bg-[#B76E79] hover:text-white transition text-[11px] font-brand tracking-[0.14em] uppercase"
          >
            <CreditCard size={12} /> {expired ? "Renew £100 hold" : "Hold £100 deposit"}
          </button>
          <button
            type="button"
            onClick={sendLink}
            disabled={busy === "link"}
            data-testid={`deposit-link-send-${booking.id}`}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-[#DDD1BE] text-[#0F0E0E] hover:bg-[#F5EFE6] transition text-[11px] font-brand tracking-[0.14em] uppercase disabled:opacity-50"
            title="Email the guest a secure link — they enter their own card, no card details over the phone"
          >
            {busy === "link" ? <Loader2 size={12} className="animate-spin" /> : <Link2 size={12} />}
            Send link
          </button>
        </div>
      )}
      {mode === "arrival" && linkUrl && (!status || expired) && (
        <div className="mt-2 rounded-lg border border-[#DDD1BE] bg-[#FBF7F1] p-3" data-testid={`deposit-link-box-${booking.id}`}>
          <p className="text-[10px] text-[#6A5F58]">
            {linkEmailed ? `Emailed to ${booking.guest_email} · ` : ""}Copy to send by text or WhatsApp:
          </p>
          <div className="mt-1.5 flex items-center gap-1.5">
            <input
              readOnly
              value={linkUrl}
              onFocus={(e) => e.target.select()}
              className="flex-1 min-w-0 bg-white border border-[#DDD1BE] rounded-md px-2 py-1.5 text-[10px] font-mono text-[#0F0E0E]"
              data-testid={`deposit-link-url-${booking.id}`}
            />
            <button
              type="button"
              onClick={copyLink}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-[#0F0E0E] text-[#F5EFE6] hover:bg-[#B76E79] transition text-[9px] font-brand tracking-[0.1em] uppercase"
              data-testid={`deposit-link-copy-${booking.id}`}
            >
              <Copy size={10} /> Copy
            </button>
            {booking.guest_phone && (
              <a
                href={smsHref(booking.guest_phone, `Hi ${firstName(booking.guest_name)}, please place your £100 refundable deposit for your Walnut Lakes stay here (it's just a card hold — no money leaves your account): ${linkUrl}`)}
                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-[#3B8C4E] text-white hover:bg-[#0F0E0E] transition text-[9px] font-brand tracking-[0.1em] uppercase"
                data-testid={`deposit-link-text-${booking.id}`}
              >
                <MessageSquareText size={10} /> Text
              </a>
            )}
          </div>
        </div>
      )}
      {mode === "arrival" && (!status || expired) && showCard && (
        <div className="mt-2 rounded-lg border border-[#B76E79] bg-[#FFF7F3] p-3 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-[10px] uppercase tracking-[0.18em] text-[#B76E79] font-semibold">Guest's card — £100 hold only, not charged</span>
            <button type="button" onClick={() => setShowCard(false)} className="text-[#6A5F58] hover:text-[#0F0E0E]" aria-label="Cancel"><X size={12} /></button>
          </div>
          <div id={cardContainerId} data-testid={`deposit-card-form-${booking.id}`} className="min-h-[90px] bg-white rounded-md border border-[#DDD1BE]" />
          <button
            type="button"
            onClick={hold}
            disabled={!cardReady || busy === "hold"}
            data-testid={`deposit-hold-submit-${booking.id}`}
            className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full bg-[#0F0E0E] text-[#F5EFE6] hover:bg-[#B76E79] transition text-[11px] font-brand tracking-[0.14em] uppercase disabled:opacity-50"
          >
            {busy === "hold" ? <Loader2 size={12} className="animate-spin" /> : <ShieldCheck size={12} />}
            {busy === "hold" ? "Placing hold…" : "Place £100 hold"}
          </button>
        </div>
      )}

      {/* Release or charge — available on BOTH arrivals and departures once a hold is active,
          so checkout refunds can be done from either list */}
      {status === "held" && !expired && (
        <div className="mt-2 grid grid-cols-2 gap-1.5">
          <button
            type="button"
            onClick={() => act("release")}
            disabled={!!busy}
            data-testid={`deposit-release-${booking.id}`}
            className="inline-flex items-center justify-center gap-1 px-2 py-2 rounded-full bg-[#3B8C4E] text-white hover:opacity-90 transition text-[10px] font-brand tracking-[0.1em] uppercase disabled:opacity-50"
          >
            {busy === "release" ? <Loader2 size={11} className="animate-spin" /> : <ShieldCheck size={11} />} Release
          </button>
          <button
            type="button"
            onClick={() => act("charge")}
            disabled={!!busy}
            data-testid={`deposit-charge-${booking.id}`}
            className="inline-flex items-center justify-center gap-1 px-2 py-2 rounded-full bg-[#8C4E58] text-white hover:opacity-90 transition text-[10px] font-brand tracking-[0.1em] uppercase disabled:opacity-50"
          >
            {busy === "charge" ? <Loader2 size={11} className="animate-spin" /> : <CreditCard size={11} />} Charge £100
          </button>
        </div>
      )}
    </div>
  );
};

export default DepositControls;
